import React, { useEffect, useState, useCallback, useRef } from "react";
import { Card, Row, Col, Statistic, Tag, Spin, message, Button, Space, Segmented, Collapse, Grid, Empty, Modal, Drawer } from "antd";
import Alert from "../../components/GxAlert";
import {
  ReloadOutlined, SettingOutlined, PlusOutlined, ThunderboltOutlined,
  DownloadOutlined, ExportOutlined, WarningOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  scDashboard, scDashboardAnalytics, scStageTrend, scWarehouseInventory,
  scDashboardStages, scPartyLedgerLog, scGetEvents,
} from "../../api/client";
import GxTable from "../../components/GxTable";
import HelpPopover from "../../components/HelpPopover";
import { fmtKg, CATEGORY_LABEL, eventLabel, statusMeta, statFmtKg } from "./scConstants";
import { toJalali } from "../../utils/dateUtils";
import { PERIOD_PRESETS, periodRange } from "./scPeriods";
import { StageFlow, LossGauge, TrendChart } from "./scCharts";
import ToleranceSettingsModal from "./ToleranceSettingsModal";
import WarehouseInventoryTable from "./WarehouseInventoryTable";
import { faNum } from "../../utils/faNumberFormat";

const { useBreakpoint } = Grid;
const KPI_LABEL = { weaving: "بافت", dyeing: "رنگرزی", transfer: "انتقال", overall: "کلِ زنجیره" };

//: کلیکِ هر مرحله کدام رویدادها را باز کند
const STAGE_DRILL = {
  yarn: "yarn_purchased",
  weaving: "raw_fabric_sent_to_dyeing",
  dyeing: "dyed_fabric_received_in_gharchak_warehouse",
  final: "dyed_fabric_received_in_gharchak_warehouse",
};
const CAT_COLOR = { yarn: "#1677ff", raw_fabric: "#13c2c2", dyed_fabric: "#722ed1", waste: "#d46b08", adjustment: "#8c8c8c" };
const faPct = (v) => (v == null ? "—" : `${faNum(Number(v), { maximumFractionDigits: 2 })}٪`);
const faN = (n) => faNum(Number(n || 0));

// شمارشِ عددی از ۰ تا مقدار در ~۱ثانیه (easing 1-(1-p)^3)، خروجی fa-IR — با احترام به reduce-motion
function useCountUp(target) {
  const [val, setVal] = useState(0);
  const raf = useRef();
  useEffect(() => {
    const to = Number(target) || 0;
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setVal(to); return; }
    const dur = 1000, t0 = performance.now();
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(to * e);
      if (p < 1) raf.current = requestAnimationFrame(step); else setVal(to);
    };
    raf.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf.current);
  }, [target]);
  return Math.round(val);
}
function CountUp({ value }) {
  const v = useCountUp(value);
  return <>{faNum(v)}</>;
}

// کارتِ KPIِ یک مرحله (با گیج + قابلِ کلیک برای drill)
/** کارتِ یک مرحلهٔ زنجیره — مقدارِ همان مرحله (نه افتِ بینِ دو مرحله).
 *  کاربر با «نخ / بافت / رنگرزی / خروجی نهایی» کار می‌کند، نه با «گذار». */
function StageKpi({ st, onClick, delay = 0 }) {
  const has = st.kg > 0;
  const loss = st.loss_kg;
  const lossColor = loss == null ? "var(--text-mute)"
    : loss > 0 ? "var(--danger)" : "var(--success)";
  return (
    <div className="gx-sc-card gx-rise" onClick={onClick}
      style={{ height: "100%", cursor: onClick ? "pointer" : "default",
               borderInlineStart: "4px solid var(--stage-yarn, var(--primary))",
               animationDelay: `${delay}s` }}>
      <div style={{ padding: "16px 18px" }}>
        <b style={{ fontSize: 14, color: "var(--text)" }}>{st.title}</b>
        <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.2, marginTop: 6,
                      color: has ? "var(--text)" : "var(--text-mute)",
                      fontFeatureSettings: "'tnum'" }}>
          {has ? fmtKg(st.kg) : "—"}
        </div>
        <div style={{ fontSize: 12, color: "var(--text-sec)", marginTop: 4,
                      fontFeatureSettings: "'tnum'" }}>
          {st.amount > 0 ? `${faN(Math.round(st.amount))} ریال` : "بدون مبلغ"}
        </div>
        {loss != null && (
          <div style={{ fontSize: 12, marginTop: 8, color: lossColor,
                        fontFeatureSettings: "'tnum'" }}>
            {loss > 0 ? "افت از مرحلهٔ قبل: " : "افزایش از مرحلهٔ قبل: "}
            {fmtKg(Math.abs(loss))}
            {st.loss_pct != null && ` (${faPct(Math.abs(st.loss_pct))})`}
          </div>
        )}
      </div>
    </div>
  );
}

function ToleranceKpi({ t, label, onClick, delay = 0 }) {
  const noData = !t.has_data;
  const status = noData ? "mute" : t.within_tolerance ? "ok" : "bad";
  const color = noData ? "var(--text-mute)" : t.within_tolerance ? "var(--success)" : "var(--danger)";
  return (
    <div className="gx-sc-card gx-rise" onClick={onClick}
      style={{ height: "100%", cursor: "pointer", borderInlineStart: `4px solid ${color}`, animationDelay: `${delay}s` }}>
      <div style={{ padding: "16px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <b style={{ fontSize: 14, color: "var(--text)" }}>{label}</b>
          <span style={{
            fontSize: 11.5, fontWeight: 700, color,
            background: `color-mix(in srgb, ${color} 15%, transparent)`, padding: "3px 10px", borderRadius: 99,
          }}>{noData ? "بدون داده" : status === "ok" ? "سالم" : "هشدار"}</span>
        </div>
        <div style={{ fontSize: 26, fontWeight: 900, color, lineHeight: 1.2, fontFeatureSettings: "'tnum'" }}>
          {noData ? "—" : `${faPct(Math.abs(t.loss_pct))}`}
          <span style={{ fontSize: 12, fontWeight: 500, color: "var(--text-mute)", marginInlineStart: 6 }}>افت</span>
        </div>
        <div style={{ fontSize: 12, color: "var(--text-sec)", margin: "2px 0 10px", fontFeatureSettings: "'tnum'" }}>
          {fmtKg(t.in_kg)} ← {fmtKg(t.out_kg)}
          {!noData && <span style={{ color, marginInlineStart: 6 }}>({fmtKg(Math.abs(t.loss_kg))})</span>}
        </div>
        <LossGauge pct={t.has_data ? Math.abs(t.loss_pct) : null} tol={t.tolerance_pct} height={48} />
      </div>
    </div>
  );
}

// آمارِ قابلِ‌کلیکِ یک مرحله (کلیک → جزئیاتِ ردیف‌ها)
function StageStat({ label, value, sub, color, onClick }) {
  return (
    <div className="gx-sc-tile" onClick={onClick} style={{ height: "100%", cursor: onClick ? "pointer" : "default" }}>
      <div style={{ fontSize: 12.5, color: "var(--text-sec)" }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: color || "var(--text)", lineHeight: 1.3, marginTop: 3, fontFeatureSettings: "'tnum'" }}>{value}</div>
      {sub != null && <div style={{ fontSize: 11.5, color: "var(--text-mute)" }}>{sub}</div>}
    </div>
  );
}

export default function SupplyChainDashboard() {
  const [d, setD] = useState(null);          // همه‌زمانه + live
  const [an, setAn] = useState(null);        // تحلیلِ بازه
  const [trend, setTrend] = useState([]);
  const [period, setPeriod] = useState("all");
  const [loading, setLoading] = useState(false);
  const [anLoading, setAnLoading] = useState(false);
  const [settings, setSettings] = useState(false);
  const [wh, setWh] = useState(null);          // مودالِ موجودیِ انبار (قرچک)
  const [stages, setStages] = useState(null);  // گزارشِ ۳مرحله‌ای
  const [stageScope, setStageScope] = useState("confirmed");
  const [detail, setDetail] = useState(null);  // درایورِ جزئیات {title}
  const [detailRows, setDetailRows] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSel, setDetailSel] = useState([]);
  const [log, setLog] = useState(null);        // مودالِ دفترِ تراکنشیِ پوشان/بافنده
  const navigate = useNavigate();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const loadStages = useCallback(async (scope) => {
    try { const r = await scDashboardStages(scope === "all" ? "all" : undefined); setStages(r.data); }
    catch { /* silent */ }
  }, []);
  useEffect(() => { loadStages(stageScope); }, [stageScope, loadStages]);

  const openDetail = async (title, params) => {
    setDetail({ title }); setDetailRows([]); setDetailSel([]); setDetailLoading(true);
    try { const r = await scGetEvents(params); setDetailRows(r.data); }
    catch { message.error("خطا در بارگذاریِ جزئیات"); }
    setDetailLoading(false);
  };
  const openLog = async (pid) => {
    try { const r = await scPartyLedgerLog(pid); setLog(r.data); }
    catch { message.error("خطا در بارگذاریِ دفترِ تراکنشی"); }
  };
  const exportSelected = () => {
    const sel = detailRows.filter((r) => detailSel.includes(r.id));
    const rows = sel.length ? sel : detailRows;
    const head = ["کد", "تاریخ", "نوع", "از", "به", "شرح", "وزن", "مبلغ", "طاقه", "مجوز", "سند", "وضعیت"];
    const body = rows.map((r) => [r.event_code, r.event_date, eventLabel(r.event_type),
      r.source_party_name || "", r.destination_party_name || "", r.item_name || r.fabric_type || "",
      r.weight_kg ?? "", r.total_amount ?? "", r.roll_count ?? "", r.permit_number || "",
      r.external_document_number || "", statusMeta(r.status).l]);
    const csv = "﻿" + [head, ...body].map((a) => a.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = "supply-chain.csv"; a.click();
  };

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [dash, tr] = await Promise.all([scDashboard(), scStageTrend(6)]);
      setD(dash.data);
      setTrend(tr.data.months || []);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در بارگذاری داشبورد"); }
    setLoading(false);
  }, []);

  const loadAnalytics = useCallback(async (key) => {
    setAnLoading(true);
    const r = periodRange(key);
    try {
      const res = await scDashboardAnalytics({ date_from: r.date_from || undefined, date_to: r.date_to || undefined });
      setAn(res.data);
    } catch (e) { message.error(e?.response?.data?.detail || "خطا در تحلیل"); }
    setAnLoading(false);
  }, []);

  useEffect(() => { loadBase(); }, [loadBase]);
  useEffect(() => { loadAnalytics(period); }, [period, loadAnalytics]);

  // drill به دفترِ رویدادها با فیلترِ نوع + بازهٔ جاری
  const drillEvent = (eventType) => {
    const r = periodRange(period);
    const qs = new URLSearchParams();
    if (eventType) qs.set("event_type", eventType);
    if (r.date_from) qs.set("date_from", r.date_from);
    if (r.date_to) qs.set("date_to", r.date_to);
    navigate(`/supply-chain/events?${qs.toString()}`);
  };
  const drillTransition = (t) => drillEvent((t.drill_out && t.drill_out[0]) || (t.drill_in && t.drill_in[0]));
  const drillParty = (pid) => navigate(`/supply-chain/events?party_id=${pid}`);
  const drillStatus = (status) => navigate(`/supply-chain/events?status=${status}`);
  // کلیک روی انبار → موجودیِ تفکیکی (پارچه/رنگ/وزن/فی/جمع)
  const openWarehouse = async (pid) => {
    try { const r = await scWarehouseInventory(pid); setWh(r.data); }
    catch { message.error("خطا در بارگذاریِ موجودیِ انبار"); }
  };

  if (!d) return <Spin spinning={loading}><div style={{ minHeight: 240 }} /></Spin>;

  const rangeLabel = periodRange(period).label;
  const transitions = an?.transitions || [];
  const stages4 = an?.stages || [];
  const overall = an?.overall;

  return (
    <div className="gx-page">
      {/* سربرگ + اقدامات */}
      <div className="gx-page-head gx-rise" style={{ animationDelay: "0s" }}>
        <div>
          <h2 className="gx-h1">داشبورد زنجیره تأمین</h2>
          <div className="gx-sub">درین ← بافندگی ← پوشان ← انبار قرچک · ردیابیِ افت و تلورانس</div>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={() => { loadBase(); loadAnalytics(period); }} loading={loading || anLoading}>تازه‌سازی</Button>
          <Button icon={<SettingOutlined />} onClick={() => setSettings(true)}>تنظیماتِ تلورانس</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate("/supply-chain/events")}>ثبت حرکت جدید</Button>
        </Space>
      </div>

      {/* فیلترِ بازهٔ زمانی (شمسی) */}
      <div className="gx-sc-card gx-rise" style={{ animationDelay: "0.04s" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", padding: "10px 12px" }}>
          <div style={{ overflowX: "auto", maxWidth: "100%" }}>
            <Segmented value={period} onChange={setPeriod}
              options={PERIOD_PRESETS.map((p) => ({ value: p.key, label: p.label }))} />
          </div>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: "var(--brand-soft)", background: "color-mix(in srgb, var(--brand-soft) 14%, transparent)", padding: "5px 11px", borderRadius: 8 }}>
            بازه: {rangeLabel}
          </span>
        </div>
      </div>

      <Spin spinning={anLoading}>
        {/* KPIهای تلورانسِ مرحله‌ای (بر اساسِ بازه) */}
        {/* چهار مرحلهٔ زنجیره: نخ ← بافت ← رنگرزی ← خروجی نهایی */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          {stages4.map((st, i) => (
            <Col xs={24} sm={12} md={6} key={st.key}>
              <StageKpi st={st} delay={0.1 + i * 0.06}
                onClick={() => drillEvent(STAGE_DRILL[st.key])} />
            </Col>
          ))}
        </Row>

        {/* افتِ هر گذار — جزئیاتِ تلورانس زیرِ مرحله‌ها */}
        {transitions.length > 0 && (
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {transitions.map((t, i) => (
              <Col xs={24} sm={12} md={overall ? 6 : 8} key={t.key}>
                <ToleranceKpi t={t} label={`افتِ ${KPI_LABEL[t.key] || t.title}`}
                  onClick={() => drillTransition(t)} delay={0.1 + i * 0.06} />
              </Col>
            ))}
            {overall && (
              <Col xs={24} sm={12} md={6}>
                <ToleranceKpi t={{ ...overall, key: "overall" }} label={KPI_LABEL.overall}
                  onClick={() => drillEvent("dyed_fabric_received_in_gharchak_warehouse")}
                  delay={0.1 + transitions.length * 0.06} />
              </Col>
            )}
          </Row>
        )}

        {/* قیفِ جریان: ورودی/خروجی و افتِ هر مرحله */}
        <div className="gx-sc-card gx-rise" style={{ marginBottom: 16, animationDelay: "0.28s" }}>
          <div className="gx-sc-card-head">
            <b className="gx-sc-card-title">جریانِ وزن و افتِ هر مرحله</b>
            <span style={{ fontSize: 12, color: "var(--text-mute)" }}>{rangeLabel}</span>
          </div>
          <div className="gx-sc-card-body">
            {transitions.length
              ? <StageFlow transitions={transitions} overall={overall} onDrill={drillTransition} />
              : <Empty description="در این بازه داده‌ای نیست" />}
          </div>
        </div>

        {/* روندِ ماهانه */}
        <div className="gx-sc-card gx-rise" style={{ marginBottom: 16, animationDelay: "0.34s" }}>
          <div className="gx-sc-card-head">
            <b className="gx-sc-card-title">روندِ ۶ ماهِ اخیر — نخِ خرید / خامِ ارسالی / رنگ‌شدهٔ دریافتی</b>
          </div>
          <div className="gx-sc-card-body">
            <TrendChart data={trend} />
          </div>
        </div>
      </Spin>

      {/* ===== وضعیت لحظه‌ای — گزارشِ سه‌مرحله (درین → بافندگی → پوشان) ===== */}
      <div className="gx-sc-card gx-rise" style={{ marginBottom: 16, border: "1px solid var(--success)", animationDelay: "0.4s" }}>
        <div className="gx-sc-card-head" style={{ background: "color-mix(in srgb, var(--success) 6%, transparent)" }}>
          <b className="gx-sc-card-title">
            <ThunderboltOutlined style={{ color: "var(--success)" }} />
            وضعیت لحظه‌ای — جریانِ سه‌مرحله
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "var(--success)", background: "color-mix(in srgb, var(--success) 15%, transparent)", padding: "3px 9px", borderRadius: 99 }}>
              <span className="gx-live-dot" />زنده
            </span>
          </b>
          <Space size={4}>
            <Segmented size="small" value={stageScope} onChange={setStageScope}
              options={[{ value: "confirmed", label: "تأییدشده" }, { value: "all", label: "همه" }]} />
            <HelpPopover title="تأییدشده یا همه؟" label="" content={
              <div style={{ fontSize: 13, lineHeight: 2 }}>
                <p style={{ marginTop: 0 }}>
                  هر فاکتور تا وقتی <b>تأیید</b> نشده، در دفتر امانی ثبت نمی‌شود و روی مانده اثر ندارد.
                </p>
                <p><b>تأییدشده</b> — فقط فاکتورهای تأییدشده. این عددِ <b>رسمی</b> است و با
                  دفتر امانی و صورت‌حسابِ طرف‌ها می‌خواند. برای تسویه‌حساب از این استفاده کنید.</p>
                <p><b>همه</b> — پیش‌نویس‌ها و نیازمندِ بررسی هم شمرده می‌شوند. برای دیدنِ
                  تصویرِ کامل قبل از تأیید مفید است، ولی <b>مبنای تسویه نیست</b>.</p>
                <p style={{ marginBottom: 0, color: "var(--text-sec)" }}>
                  اگر این دو عدد فرق داشتند، یعنی فاکتورِ تأییدنشده دارید — در «فاکتورها» ببینید.
                </p>
              </div>} />
          </Space>
        </div>
        <div className="gx-sc-card-body">
        {stages ? (
          <Space direction="vertical" size={18} style={{ width: "100%" }}>
            {/* ① درین */}
            <div>
              <div style={{ fontWeight: 700, color: "var(--stage-yarn)", marginBottom: 10, fontSize: 13.5 }}>① خرید نخ</div>
              <div className="gx-sc-grid-4">
                {/* هر بافنده یک کاشی — بافندهٔ تازه از «طرف‌حساب‌ها» خودکار اضافه می‌شود */}
                {(stages.stage1.by_knitter || []).map((k) => (
                  <StageStat key={k.party_id} label={`نخ ← ${k.name}`} color="var(--text)" value={fmtKg(k.kg)}
                    onClick={() => openDetail(`خرید نخِ ${k.name}`, { event_type: "yarn_purchased", party_id: k.party_id })} />
                ))}
                <StageStat label="جمعِ نخِ خریداری‌شده" color="var(--text)" value={fmtKg(stages.stage1.yarn_total)}
                  onClick={() => openDetail("کلِ خرید نخ", { event_type: "yarn_purchased" })} />
                <StageStat label="مبلغِ خرید (ریال)" color="var(--text)" value={faN(Math.round(stages.stage1.amount))} />
              </div>
            </div>
            {/* ② بافندگی */}
            <div>
              <div style={{ fontWeight: 700, color: "var(--stage-raw)", marginBottom: 10, fontSize: 13.5 }}>② بافندگی — ورودیِ نخ / خروجیِ پارچهٔ خام</div>
              <div className="gx-sc-grid-2">
                {(stages.stage2.knitters || []).map((k) => (
                  <div className="gx-sc-tile" key={k.party_id} style={{ height: "100%" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <b style={{ fontSize: 13.5, color: "var(--text)" }}>{k.name}</b>
                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => openLog(k.party_id)}>دفترِ بافنده ›</Button>
                    </div>
                    <div style={{ display: "flex", gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11.5, color: "var(--text-mute)" }}>ورودیِ نخ</div>
                        <div style={{ cursor: "pointer", fontWeight: 700, color: "var(--text)", fontSize: 15, fontFeatureSettings: "'tnum'" }}
                          onClick={() => openDetail(`خرید نخِ ${k.name}`, { event_type: "yarn_purchased", party_id: k.party_id })}>{fmtKg(k.yarn_input_kg)}</div>
                        {/* تفکیکِ ورودی، تا معلوم باشد چرا عدد با «خرید» فرق دارد */}
                        {k.opening_balance_kg > 0 && (
                          <div style={{ fontSize: 10.5, color: "var(--text-mute)", marginTop: 2, fontFeatureSettings: "'tnum'" }}>
                            خرید {fmtKg(k.yarn_purchased_kg)} + اول دوره {fmtKg(k.opening_balance_kg)}
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11.5, color: "var(--text-mute)" }}>خروجیِ خام</div>
                        <div style={{ cursor: "pointer", fontWeight: 700, color: "var(--text)", fontSize: 15, fontFeatureSettings: "'tnum'" }}
                          onClick={() => openDetail(`خروجیِ بافندگیِ ${k.name}`, { event_type: "raw_fabric_sent_to_dyeing", party_id: k.party_id })}>{fmtKg(k.raw_output_kg)}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11.5, color: "var(--text-mute)" }}>مانده/اختلافِ تقریبی</div>
                        <div style={{ fontWeight: 700, color: k.diff_kg < 0 ? "var(--danger)" : "var(--success)", fontSize: 15, fontFeatureSettings: "'tnum'" }}>{fmtKg(k.diff_kg)}</div>
                      </div>
                    </div>
                    {k.warning && <Alert type="warning" showIcon style={{ marginTop: 8, padding: "4px 8px", fontSize: 12 }} message={k.warning} />}
                  </div>
                ))}
              </div>
            </div>
            {/* ③ پوشان */}
            <div>
              <div style={{ fontWeight: 700, color: "var(--stage-dyed)", marginBottom: 10, fontSize: 13.5, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                ③ رنگرزی — ورودیِ خام / خروجیِ رنگ‌شده
                <Button type="link" size="small" style={{ padding: 0 }} onClick={() => stages.nodes.pooshan && openLog(stages.nodes.pooshan)}>دفترِ پوشان ›</Button>
              </div>
              <div className="gx-sc-grid-4">
                <StageStat label="ورودیِ پارچهٔ خام" color="var(--text)" value={fmtKg(stages.stage3.raw_input_kg)}
                  onClick={() => openDetail("ورودیِ خامِ پوشان", { event_type: "raw_fabric_sent_to_dyeing" })} />
                <StageStat label="خروجیِ رنگ‌شده" color="var(--text)" value={fmtKg(stages.stage3.dyed_output_kg)}
                  onClick={() => openDetail("خروجیِ پوشان", { event_type: "dyed_fabric_received_in_gharchak_warehouse" })} />
                <StageStat label="ماندهٔ نزدِ رنگرزی" value={fmtKg(stages.stage3.balance_kg)}
                  color={stages.stage3.balance_kg < 0 ? "var(--danger)" : "var(--success)"} />
                <StageStat label="مبلغِ رنگرزی (ریال)" color="var(--text)" value={faN(Math.round(stages.stage3.dyed_amount))} />
              </div>
              {stages.stage3.warning && <Alert type="error" showIcon style={{ marginTop: 12 }} message={stages.stage3.warning} />}
            </div>
            {/* انبار قرچک — موجودیِ تفکیکی */}
            <div>
              <div style={{ fontWeight: 700, color: "var(--stage-received)", marginBottom: 10, fontSize: 13.5 }}>④ انبار قرچک</div>
              <Button onClick={() => stages.nodes.gharchak && openWarehouse(stages.nodes.gharchak)}>مشاهدهٔ موجودیِ تفکیکیِ انبار قرچک ›</Button>
            </div>
          </Space>
        ) : <Empty description="داده‌ای نیست" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
        </div>
      </div>

      {/* شمارنده‌های سلامتِ داده (قابلِ کلیک) */}
      <Row gutter={[16, 16]} className="gx-rise" style={{ animationDelay: "0.46s" }}>
        <Col xs={12} md={6}>
          <div className="gx-sc-card" onClick={() => drillStatus("needs_review")} style={{ cursor: "pointer", padding: "16px 18px" }}>
            <div style={{ fontSize: 12.5, color: "var(--text-sec)" }}>نیازمند بررسی</div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4, color: d.needs_review_count ? "var(--gold)" : "var(--text)", fontFeatureSettings: "'tnum'" }}><CountUp value={d.needs_review_count} /></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="gx-sc-card" onClick={() => drillStatus("disputed")} style={{ cursor: "pointer", padding: "16px 18px" }}>
            <div style={{ fontSize: 12.5, color: "var(--text-sec)" }}>مغایر</div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4, color: d.disputed_count ? "var(--danger)" : "var(--text)", fontFeatureSettings: "'tnum'" }}><CountUp value={d.disputed_count} /></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="gx-sc-card" onClick={() => drillStatus("draft")} style={{ cursor: "pointer", padding: "16px 18px" }}>
            <div style={{ fontSize: 12.5, color: "var(--text-sec)" }}>پیش‌نویس (تأییدنشده)</div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4, color: "var(--text)", fontFeatureSettings: "'tnum'" }}><CountUp value={d.draft_count} /></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="gx-sc-card" onClick={() => navigate("/supply-chain/reports")} style={{ cursor: "pointer", padding: "16px 18px" }}>
            <div style={{ fontSize: 12.5, color: "var(--text-sec)" }}>بدون فاکتور حسابداری</div>
            <div style={{ fontSize: 26, fontWeight: 900, marginTop: 4, color: "var(--text)", fontFeatureSettings: "'tnum'" }}><CountUp value={d.documents_without_invoice_count} /></div>
          </div>
        </Col>
      </Row>

      {/* موجودی انبار قرچک با فی تمام‌شدهٔ زنجیره */}
      <div className="gx-sc-card gx-rise" style={{ marginTop: 4, padding: "16px 18px", animationDelay: "0.5s" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <span style={{ fontWeight: 800, fontSize: 14 }}>📦 موجودی انبار قرچک (فی تمام‌شدهٔ زنجیره)</span>
          <Button size="small" onClick={() => navigate("/supply-chain/reports")}>گزارش کامل ›</Button>
        </div>
        <WarehouseInventoryTable compact />
      </div>

      {/* راهنمای مراحل (تاشو) */}
      <Collapse style={{ marginTop: 4 }} items={[{
        key: "guide",
        label: "راهنمای مراحلِ زنجیره و تلورانس",
        children: (
          <div style={{ direction: "rtl", lineHeight: 1.9, fontSize: 13 }}>
            <p><b style={{ color: "var(--stage-yarn)" }}>۱) خرید نخ (درین):</b> نخِ خریداری‌شده واردِ زنجیره می‌شود.</p>
            <p><b style={{ color: "var(--stage-raw)" }}>۲) بافت (نوربخش/خوشباف):</b> نخ به پارچهٔ خام تبدیل می‌شود؛ <b>افتِ بافت</b> = نخِ ورودی منهای پارچهٔ خام.</p>
            <p><b style={{ color: "var(--stage-dyed)" }}>۳) رنگرزی (پوشان):</b> پارچهٔ خام رنگ می‌شود؛ <b>افتِ رنگرزی</b> = خامِ ارسالی منهای رنگ‌شدهٔ دریافتی.</p>
            <p><b style={{ color: "var(--stage-received)" }}>۴) انبار قرچک:</b> پارچهٔ رنگ‌شده تحویل گرفته می‌شود.</p>
            <p style={{ color: "var(--text-mute)" }}>افتِ بیش از آستانهٔ هر مرحله (قابلِ‌تنظیم از «تنظیماتِ تلورانس») قرمز نشان داده می‌شود.
              افت‌ها از روی <b>مجموعِ هر مرحله</b> در بازهٔ انتخابی محاسبه می‌شوند.</p>
          </div>
        ),
      }]} />

      <ToleranceSettingsModal open={settings} onClose={() => setSettings(false)}
        onSaved={() => { loadBase(); loadAnalytics(period); }} />

      {/* مودالِ موجودیِ تفکیکیِ انبار (پارچه/رنگ/وزن/طاقه/فی/ارزش + جمعِ کل) */}
      <Modal open={!!wh} onCancel={() => setWh(null)} footer={null} width={780}
        title={wh ? `موجودیِ تفکیکیِ ${wh.party_name}` : ""}>
        {wh && (
          <>
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col xs={12}><Card size="small"><Statistic title="جمعِ کل (کیلوگرم)" value={faN(Math.round(wh.total_kg))} /></Card></Col>
              <Col xs={12}><Card size="small"><Statistic title="ارزشِ کل (ریال)" value={faN(Math.round(wh.total_amount))} /></Card></Col>
            </Row>
            {wh.rows.length ? (
              <GxTable dataSource={wh.rows} rowKey={(r, i) => i} size="small" pagination={false}
                scroll={{ x: "max-content" }}
                columns={[
                  { title: "نوع", dataIndex: "item_category", render: (v) => <Tag color={CAT_COLOR[v]}>{CATEGORY_LABEL[v] || v}</Tag> },
                  { title: "نوع پارچه/کالا", dataIndex: "fabric_type", render: (v) => v || "—" },
                  { title: "رنگ", dataIndex: "color", render: (v) => v || "—" },
                  { title: "وزن (کیلو)", dataIndex: "balance_kg", align: "center", render: (v) => fmtKg(v) },
                  { title: "طاقه", dataIndex: "roll_count", align: "center", render: (v) => (v != null ? faN(v) : "—") },
                  { title: "فی (ریال)", dataIndex: "unit_price", align: "center", render: (v) => (v ? faN(Math.round(v)) : "—") },
                  { title: "ارزش (ریال)", dataIndex: "total_amount", align: "center", render: (v) => (v ? faN(Math.round(v)) : "—") },
                  { title: "فی تمام‌شده (زنجیره)", dataIndex: "full_cost_per_kg", align: "center",
                    render: (v, r) => (v == null ? "—" : <span><b style={{ color: "var(--success, #107C41)" }}>{faN(Math.round(v))}</b>{r.full_cost_estimated && <Tag color="orange" style={{ marginRight: 6 }}>تقریبی</Tag>}</span>) },
                  { title: "ارزش تمام‌شده", dataIndex: "full_total_value", align: "center", render: (v) => (v ? faN(Math.round(v)) : "—") },
                ]} />
            ) : <Empty description="موجودیِ این انبار صفر است" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </>
        )}
      </Modal>

      {/* درایورِ جزئیاتِ ردیف‌ها — انتخابِ ردیفی + جمعِ انتخاب‌شده‌ها + خروجی CSV */}
      <Drawer title={detail?.title || "جزئیات"} open={!!detail} onClose={() => setDetail(null)}
        placement={isMobile ? "bottom" : "right"} width={isMobile ? "100%" : 780} height={isMobile ? "90%" : undefined}>
        {(() => {
          const sel = detailRows.filter((r) => detailSel.includes(r.id));
          const base = sel.length ? sel : detailRows;
          const sum = (f) => base.reduce((a, r) => a + (Number(r[f]) || 0), 0);
          return (
            <>
              <Space wrap style={{ marginBottom: 10 }}>
                <Button icon={<DownloadOutlined />} onClick={exportSelected}>خروجی CSV ({faN(sel.length || detailRows.length)})</Button>
                <Button icon={<ExportOutlined />} onClick={() => navigate("/supply-chain/events")}>ویرایش/مدیریت در دفتر</Button>
              </Space>
              <div style={{ background: "var(--surface-2)", borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 13 }}>
                {sel.length ? `${faN(sel.length)} ردیفِ انتخاب‌شده` : `${faN(detailRows.length)} ردیف`} —
                <b style={{ marginInlineStart: 6 }}>وزن: {fmtKg(sum("weight_kg"))}</b> ·
                <b style={{ marginInlineStart: 6 }}>مبلغ: {faN(Math.round(sum("total_amount")))} ریال</b> ·
                طاقه: {faN(sum("roll_count"))} · کارتن: {faN(sum("carton_count"))}
              </div>
              <GxTable dataSource={detailRows} rowKey="id" size="small" loading={detailLoading}
                pagination={{ pageSize: 15 }} scroll={{ x: "max-content" }}
                rowSelection={{ selectedRowKeys: detailSel, onChange: setDetailSel }}
                columns={[
                  { title: "تاریخ", dataIndex: "event_date", render: (v) => toJalali(v) },
                  { title: "شرح", dataIndex: "item_name", ellipsis: true, render: (_, r) => r.item_name || [r.fabric_type || r.yarn_count, r.color].filter(Boolean).join(" — ") || "—" },
                  { title: "از", dataIndex: "source_party_name", render: (v) => v || "—" },
                  { title: "به", dataIndex: "destination_party_name", render: (v) => v || "—" },
                  { title: "وزن", dataIndex: "weight_kg", align: "center", render: (v) => fmtKg(v) },
                  { title: "مبلغ", dataIndex: "total_amount", align: "center", render: (v) => (v ? faN(Math.round(v)) : "—") },
                  { title: "وضعیت", dataIndex: "status", render: (v, r) => (<Space size={2}><Tag color={statusMeta(v).c}>{statusMeta(v).l}</Tag>{r.needs_completion && <Tag color="gold" style={{ fontSize: 10 }}>تکمیل</Tag>}</Space>) },
                  { title: "سند/مجوز", render: (_, r) => `${r.external_document_number || "—"} / ${r.permit_number || "—"}` },
                ]} />
            </>
          );
        })()}
      </Drawer>

      {/* مودالِ دفترِ تراکنشیِ پوشان/بافنده (ورودی/خروجی/ماندهٔ تجمعی) */}
      <Modal open={!!log} onCancel={() => setLog(null)} footer={null} width={820}
        title={log ? `دفترِ تراکنشیِ ${log.party_name}` : ""}>
        {log && (
          <>
            {log.negative_balance && <Alert type="warning" showIcon icon={<WarningOutlined />} style={{ marginBottom: 10 }}
              message="ماندهٔ منفی"
              description={log.has_opening_balance
                ? "خروجی بیشتر از ورودیِ ثبت‌شده است؛ ورودی‌های این طرف را بررسی کنید."
                : "برای این طرف «مانده اول دوره» ثبت نشده است؛ تا وقتی ثبت نشود مانده منفی می‌ماند."} />}
            <Row gutter={12} style={{ marginBottom: 12 }}>
              <Col xs={8}><Card size="small"><Statistic title="جمعِ ورودی" value={log.in_total ?? 0} formatter={statFmtKg} /></Card></Col>
              <Col xs={8}><Card size="small"><Statistic title="جمعِ خروجی" value={log.out_total ?? 0} formatter={statFmtKg} /></Card></Col>
              <Col xs={8}><Card size="small"><Statistic title="مانده" value={log.balance ?? 0} formatter={statFmtKg}
                valueStyle={{ color: log.balance < 0 ? "#cf1322" : "#389e0d" }} /></Card></Col>
            </Row>
            {log.rows.length ? (
              <GxTable dataSource={log.rows} rowKey={(r) => r.ledger_id} size="small" pagination={false} scroll={{ x: "max-content" }}
                columns={[
                  { title: "نوع ردیف", dataIndex: "row_type", render: (v, r) => <Tag color={r.direction === "in" ? "cyan" : r.direction === "out" ? "purple" : "default"}>{v}</Tag> },
                  { title: "تاریخ", dataIndex: "event_date", render: (v) => toJalali(v) },
                  { title: "سند", dataIndex: "document_number", render: (v) => v || "—" },
                  { title: "پارچه/رنگ", render: (_, r) => [r.fabric_type, r.color].filter(Boolean).join(" — ") || "—" },
                  { title: "وزن ورودی", dataIndex: "weight_in", align: "center", render: (v) => (v != null ? fmtKg(v) : "—") },
                  { title: "وزن خروجی", dataIndex: "weight_out", align: "center", render: (v) => (v != null ? fmtKg(v) : "—") },
                  { title: "ماندهٔ تجمعی", dataIndex: "running_balance", align: "center", render: (v) => <b style={{ color: v < 0 ? "#cf1322" : "#1F4E79" }}>{fmtKg(v)}</b> },
                  { title: "وضعیت", dataIndex: "status", render: (v) => <Tag color={statusMeta(v).c}>{statusMeta(v).l}</Tag> },
                ]} />
            ) : <Empty description="تراکنشی نیست" image={Empty.PRESENTED_IMAGE_SIMPLE} />}
          </>
        )}
      </Modal>
    </div>
  );
}
